import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Mail, Phone, MapPin } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const LOGO_URL = '/images/logo.svg';

export function Footer() {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-warm-100 border-t border-warm-200">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & Tagline */}
          <div className="lg:col-span-1">
            <Link to="/" data-testid="footer-logo">
              <img
                src={LOGO_URL}
                alt="Vera Hilário Fotografia"
                className="h-20 w-auto mb-4"
              />
            </Link>
            <p className="text-warm-600 text-base leading-relaxed">
              {t.footer.tagline}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-accent font-heading font-semibold text-sm tracking-wide mb-6">
              Menu
            </h4>
            <nav className="flex flex-col gap-3">
              <Link
                to="/"
                data-testid="footer-home"
                className="text-warm-600 hover:text-accent transition-colors text-base"
              >
                {t.nav.home}
              </Link>
              <Link
                to="/gallery"
                data-testid="footer-gallery"
                className="text-warm-600 hover:text-accent transition-colors text-base"
              >
                {t.nav.gallery}
              </Link>
              <Link
                to="/services"
                data-testid="footer-services"
                className="text-warm-600 hover:text-accent transition-colors text-base"
              >
                {t.nav.services}
              </Link>
              <Link
                to="/about"
                data-testid="footer-about"
                className="text-warm-600 hover:text-accent transition-colors text-base"
              >
                {t.nav.about}
              </Link>
              <Link
                to="/contact"
                data-testid="footer-contact"
                className="text-warm-600 hover:text-accent transition-colors text-base"
              >
                {t.nav.contact}
              </Link>
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-accent font-heading font-semibold text-sm tracking-wide mb-6">
              {t.contact.title}
            </h4>
            <div className="flex flex-col gap-4">
              <a
                href="mailto:verahilariofotografia@gmail.com"
                data-testid="footer-email"
                className="flex items-center gap-3 text-warm-600 hover:text-accent transition-colors text-base"
              >
                <Mail className="w-4 h-4 text-accent" />
                <span>verahilariofotografia@gmail.com</span>
              </a>
              <a
                href="tel:+351913446144"
                data-testid="footer-phone"
                className="flex items-center gap-3 text-warm-600 hover:text-accent transition-colors text-base"
              >
                <Phone className="w-4 h-4 text-accent" />
                <span>+351 913 446 144</span>
              </a>
              <div className="flex items-center gap-3 text-warm-600 text-base">
                <MapPin className="w-4 h-4 text-accent" />
                <span>Atalaia, Montijo</span>
              </div>
            </div>
          </div>

          {/* Social */}
          <div>
            <h4 className="text-accent font-heading font-semibold text-sm tracking-wide mb-6">
              {t.contact.info.social}
            </h4>
            <a
              href="https://www.instagram.com/verahilariofotografia/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              className="inline-flex items-center gap-3 text-warm-600 hover:text-accent transition-colors group"
            >
              <div className="w-10 h-10 rounded-full bg-warm-200 group-hover:bg-accent/10 flex items-center justify-center transition-colors">
                <Instagram className="w-5 h-5 group-hover:text-accent" />
              </div>
              <span className="text-base">@verahilariofotografia</span>
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-warm-200 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-warm-500 text-xs">
            © {currentYear} Vera Hilário Fotografia. {t.footer.rights}
          </p>
          <div className="flex gap-6">
            <Link
              to="/privacy"
              className="text-warm-500 hover:text-accent text-xs transition-colors"
            >
              {t.footer.privacy}
            </Link>
            <Link
              to="/terms"
              className="text-warm-500 hover:text-accent text-xs transition-colors"
            >
              {t.footer.terms}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
